import { addDays, BarChart, PageHead, todayIso } from "@getaways/extranet-ui";
import { FilterBar, useFilters, useOptions } from "../lib/filters";
import { eur } from "../lib/format";
import { OrderList } from "../lib/lists";
import { compactEur, filterOf, ResultTable, useQuery } from "../lib/query";

/** Money in and out (docs/36 §7): cash by day and method, refunds, balances owed, supplier payables, commissions. */
export function FinancePage() {
  const [filters, setFilters] = useFilters("finance");
  const options = useOptions();
  const f = filterOf(filters);
  const cash = useQuery({ table: "movements", measures: ["amount"], dimensions: ["day", "kind"], filter: f, kinds: ["payment", "refund", "chargeback"], sort: "day", dir: "asc", limit: 2000 });
  const days = [...new Set((cash.data?.rows ?? []).map((r) => String(r.day)))].sort();
  const value = (day: string, kind: string) => Number(cash.data?.rows.find((r) => r.day === day && r.kind === kind)?.amount ?? 0);
  const today = todayIso();
  return (
    <>
      <PageHead title="Finance" sub="Money received and returned, balances owed, what we owe suppliers and partners." />
      <div className="card" style={{ padding: 0, marginBottom: 14 }}>
        <FilterBar filters={filters} onChange={setFilters} options={options} supplierFilter={false} statusFilter={false} />
      </div>
      {days.length > 0 && (
        <section className="card" style={{ marginBottom: 14 }}>
          <h2 style={{ marginTop: 0, fontSize: 15 }}>Cash per day</h2>
          <BarChart label="Payments and refunds per day" categories={days.map((d) => d.slice(5))} categoryTitle={(i) => days[i]!} format={(v) => eur(v)} axisFormat={compactEur} series={[
            { label: "Received", color: "var(--series-2)", values: days.map((d) => value(d, "payment")) },
            { label: "Refunded", color: "var(--series-1)", values: days.map((d) => value(d, "refund") + value(d, "chargeback")) },
          ]} />
        </section>
      )}
      <div className="grid-2">
        <ResultTable title="Received and returned, by method" spec={{ table: "movements", measures: ["movements", "amount"], dimensions: ["kind", "method"], filter: f, kinds: ["payment", "refund", "chargeback"] }} />
        <ResultTable title="By provider" spec={{ table: "movements", measures: ["movements", "amount"], dimensions: ["provider"], filter: f, kinds: ["payment"] }} />
        <ResultTable
          title="Supplier payables by month of service"
          spec={{ table: "items", measures: ["services", "item_cost"], dimensions: ["service_month", "supplier"], filter: { from: today, to: addDays(today, 540), basis: "departure", status: "sales", ...(filters.brand ? { brands: [filters.brand] } : {}) }, sort: "service_month", dir: "asc", limit: 500 }}
          empty="Nothing owed to suppliers for services ahead."
        />
        <ResultTable title="Partner commissions" spec={{ table: "orders", measures: ["orders", "sell", "commission"], dimensions: ["partner"], filter: { ...f, status: "sales" } }} label={(c, v) => (c === "partner" && v === null ? "Direct (no partner)" : undefined)} />
        <ResultTable title="Balances owed by month of departure" spec={{ table: "orders", measures: ["orders", "outstanding"], dimensions: ["departure_month"], filter: { from: addDays(today, -60), to: addDays(today, 540), basis: "departure", status: "sales", owing: true }, sort: "departure_month", dir: "asc" }} empty="No balance owed." />
        <OrderList title="Owing, departing soonest" params={{ status: "sales", owing: "true", from: today, to: addDays(today, 365), basis: "departure", sort: "departureDate", dir: "asc", brands: filters.brand || undefined }} money empty="No balance owed." />
      </div>
    </>
  );
}
